import type { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import cx from 'clsx';
import dayjs from 'dayjs';
import { Accordion, FormattedDate } from '@repo/design-system-kit';
import styles from './toggle-section.module.css';

export interface ToggleSectionProps {
  creditorName: string;
  creditorIdentifier: string;
  address: string;
  reference: string;
  signatureDate: string;
  uniqueMandateReference: string;
}

export function ToggleSection({
  creditorName,
  creditorIdentifier,
  address,
  reference,
  signatureDate,
  uniqueMandateReference,
}: ToggleSectionProps): ReactNode {
  const intl = useIntl();

  const formatSignatureDate = () => {
    const date = dayjs(signatureDate);
    if (date.isValid()) {
      return <FormattedDate date={date.toISOString()} locale={intl.locale} token="compact" />;
    }
    return signatureDate;
  };

  return (
    <div data-test-sdd-payment-link-toggle-section>
      <Accordion.Single
        className={styles.accordion}
        header={
          <h2 className="title-4">
            {intl.formatMessage({ id: 'sdd-collections.payment-link-page.mandate.title' })}
          </h2>
        }
        variant="default"
      >
        <div className={styles.container}>
          <div className={styles.sepaLogo}>
            <img alt="" aria-hidden="true" src="/icon/sepa-logo.svg" />
          </div>

          <ul className={styles.features}>
            <li>
              <div className={cx(styles.key, 'body-2')}>
                <FormattedMessage id="sdd-collections.payment-link-page.mandate.creditor-name" />
              </div>
              <div className="body-1">{creditorName}</div>
            </li>
            <li>
              <div className={cx(styles.key, 'body-2')}>
                <FormattedMessage id="sdd-collections.payment-link-page.mandate.creditor-identifier" />
              </div>
              <div className="body-1">{creditorIdentifier}</div>
            </li>
            <li>
              <div className={cx(styles.key, 'body-2')}>
                <FormattedMessage id="labels.address" />
              </div>
              <div className="body-1">{address}</div>
            </li>
            <li>
              <div className={cx(styles.key, 'body-2')}>
                <FormattedMessage id="labels.reference" />
              </div>
              <div className="body-1">{reference}</div>
            </li>
            <li>
              <div className={cx(styles.key, 'body-2')}>
                <FormattedMessage id="labels.date" />
              </div>
              <div className="body-1">{formatSignatureDate()}</div>
            </li>
            <li>
              <div className={cx(styles.key, 'body-2')}>
                <FormattedMessage id="sdd-collections.details.umr" />
              </div>
              <div className="body-1">{uniqueMandateReference}</div>
            </li>
            <li>
              <div className={cx(styles.key, 'body-2')}>
                <FormattedMessage id="sdd-collections.payment-link-page.mandate.type" />
              </div>
              <div className="body-1">
                <FormattedMessage id="sdd-collections.types.recurring" />
              </div>
            </li>
          </ul>
        </div>
      </Accordion.Single>
    </div>
  );
}
